import * as React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface SmartSpellcheckInputProps extends Omit<React.ComponentProps<"input">, 'onChange'> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface SmartSpellcheckTextareaProps extends Omit<React.ComponentProps<"textarea">, 'onChange'> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

/**
 * Input component that only enables spellcheck after user types a space.
 * This prevents red underlines on incomplete words while still catching typos.
 */
export const SmartSpellcheckInput = React.forwardRef<HTMLInputElement, SmartSpellcheckInputProps>(
  ({ value, onChange, className, ...props }, ref) => {
    const [enableSpellcheck, setEnableSpellcheck] = React.useState(false);
    const previousValueRef = React.useRef(value);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const previousValue = previousValueRef.current;

      // Enable spellcheck if user just typed a space
      if (newValue.length > previousValue.length && newValue.endsWith(' ')) {
        setEnableSpellcheck(true);
      } 
      // Disable spellcheck when user is actively typing (not a space)
      else if (newValue.length > previousValue.length && !newValue.endsWith(' ')) {
        setEnableSpellcheck(false);
      }
      // Keep spellcheck enabled if text contains spaces
      else if (newValue.includes(' ')) {
        setEnableSpellcheck(true);
      }
      // Disable if no spaces in the text (still on first word)
      else {
        setEnableSpellcheck(false);
      }

      previousValueRef.current = newValue;
      onChange(e);
    };

    return (
      <Input
        ref={ref}
        value={value}
        onChange={handleChange}
        spellCheck={enableSpellcheck}
        className={className}
        {...props}
      />
    );
  }
);

SmartSpellcheckInput.displayName = "SmartSpellcheckInput";

/**
 * Textarea component that only enables spellcheck after user types a space.
 * This prevents red underlines on incomplete words while still catching typos.
 */
export const SmartSpellcheckTextarea = React.forwardRef<HTMLTextAreaElement, SmartSpellcheckTextareaProps>(
  ({ value, onChange, className, ...props }, ref) => {
    const [enableSpellcheck, setEnableSpellcheck] = React.useState(false);
    const previousValueRef = React.useRef(value);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const previousValue = previousValueRef.current;

      // Enable spellcheck if user just typed a space
      if (newValue.length > previousValue.length && newValue.endsWith(' ')) {
        setEnableSpellcheck(true);
      } 
      // Disable spellcheck when user is actively typing (not a space)
      else if (newValue.length > previousValue.length && !newValue.endsWith(' ')) {
        setEnableSpellcheck(false);
      }
      // Keep spellcheck enabled if text contains spaces
      else if (newValue.includes(' ')) {
        setEnableSpellcheck(true);
      }
      // Disable if no spaces in the text (still on first word)
      else {
        setEnableSpellcheck(false);
      }

      previousValueRef.current = newValue;
      onChange(e);
    };

    return (
      <Textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        spellCheck={enableSpellcheck}
        className={className}
        {...props}
      />
    );
  }
);

SmartSpellcheckTextarea.displayName = "SmartSpellcheckTextarea";
